/**
 * useChildren - Hook for fetching children from Firestore
 *
 * Provides cached, type-safe access to children data with automatic
 * loading and error states via React Query.
 *
 * Usage:
 * ```tsx
 * const { children, isLoading, error, refetch } = useChildren();
 * ```
 */

import { where, QueryConstraint } from 'firebase/firestore';
import { useFirestoreQuery, useFirestoreDocument, schoolCollection } from './useFirestoreQuery';
import { queryKeys } from './queryKeys';
import { useAuth } from './useAuth';
import type { Child } from '@shared/types';

export interface UseChildrenOptions {
  /** Override schoolId (defaults to user's schoolId) */
  schoolId?: string;
  /** Override familyId (defaults to user's familyId) */
  familyId?: string;
  /** Whether to fetch (defaults to true when schoolId and familyId are available) */
  enabled?: boolean;
}

export interface UseChildrenResult {
  /** List of children */
  children: Child[];
  /** Loading state */
  isLoading: boolean;
  /** Error if query failed */
  error: Error | null;
  /**
   * Обновяването падна, но данните от ПО-РАННО зареждане от сървъра (в тази сесия) са запазени
   * (`isRefetchError`). Екранът ги показва с бележка — НЕ ги сменя с панел за грешка: фоново
   * обновяване (фокус на прозореца) иначе затваря отворената форма за подпис (ревю кр.2, 20.10).
   * `error` е попълнен и в двата случая; „първото зареждане падна“ = `error && !refreshFailed`.
   */
  refreshFailed: boolean;
  /** Refetch function */
  refetch: () => void;
  /** Whether data is being refetched in background */
  isRefetching: boolean;
}

/**
 * Hook for fetching family children
 */
export function useChildren(options: UseChildrenOptions = {}): UseChildrenResult {
  const { customClaims } = useAuth();

  const schoolId = options.schoolId || customClaims?.schoolId;
  const familyId = options.familyId || customClaims?.familyId;

  // Build query constraints
  const constraints: QueryConstraint[] = [];

  if (familyId) {
    constraints.push(where('familyId', '==', familyId));
  }

  const {
    data = [],
    isLoading,
    error,
    refetch,
    isRefetching,
    isRefetchError,
  } = useFirestoreQuery<Child>({
    queryKey: queryKeys.school.children.byFamily(schoolId || '', familyId || ''),
    collectionPath: schoolCollection(schoolId || '', 'children'),
    constraints,
    enabled: options.enabled ?? (!!schoolId && !!familyId),
    idField: 'childId',
    // Без сървър → грешка, не „Очаквайте да бъдете свързани с децата си“ (19.10, № 1116).
    requireServer: true,
  });

  return {
    children: data,
    isLoading,
    error,
    refreshFailed: isRefetchError,
    refetch,
    isRefetching,
  };
}

/**
 * Hook for fetching a single child by ID
 */
export function useChild(childId: string | undefined) {
  const { customClaims } = useAuth();
  const schoolId = customClaims?.schoolId;

  // Single-doc GET (not a list query). The previous list query
  // `where('childId','==',childId)` was rejected by the children.list rule for
  // parents (2026-06-10 production bug "Детето не е намерено"): that rule
  // requires the query to be family-scoped (`where('familyId','==',own)`), but
  // a childId filter cannot prove own-family, so Firestore denies the whole
  // list. A single-doc get passes children.GET (parent own-family check on
  // resource.data). [rules audit A1]
  const { data, isLoading, error, refetch } = useFirestoreDocument<Child>({
    queryKey: queryKeys.school.children.single(schoolId || '', childId || ''),
    documentPath: `schools/${schoolId || ''}/children/${childId || ''}`,
    enabled: !!schoolId && !!childId,
    transform: (d, id) => ({ ...d, childId: id } as Child),
  });

  return {
    child: data || null,
    isLoading,
    error,
    refetch,
  };
}
